import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config`);
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Logo/Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#2B4C3B] to-[#3A604D] items-center justify-center p-12">
        {config?.clinic_logo ? (
          <div className="max-w-md w-full">
            <img 
              src={config.clinic_logo} 
              alt={config.clinic_name || 'Logo'} 
              className="w-full h-auto object-contain filter drop-shadow-2xl"
            />
            {config.clinic_name && (
              <h2 className="text-white text-3xl font-bold text-center mt-8">
                {config.clinic_name}
              </h2>
            )}
          </div>
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/30887567/pexels-photo-30887567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            }}
          >
            <div className="absolute inset-0 bg-[#2B4C3B]/60 backdrop-blur-sm"></div>
          </div>
        )}
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FDFBF7]">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#2B4C3B] tracking-tight">Bienvenido</h1>
            <p className="mt-2 text-[#6A736B]">
              {config?.clinic_name || 'Sistema de Gestión Quirúrgica'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="login-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#1A1C18]">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 bg-transparent border-[#E8E5DD] focus:ring-2 focus:ring-[#2B4C3B] focus:border-transparent"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#1A1C18]">Contraseña</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 bg-transparent border-[#E8E5DD] focus:ring-2 focus:ring-[#2B4C3B] focus:border-transparent"
                  placeholder="••••••••"
                />
                <div className="text-right mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2B4C3B] hover:text-[#3A604D] font-medium transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="error-message">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full bg-[#2B4C3B] hover:bg-[#3A604D] text-white rounded-lg h-11 transition-colors duration-200"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ingresando...
                </>
              ) : (
                'Ingresar'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}